/**
 * Typography Loader
 *
 * Fetches admin-customised typography scale (base font size, small text,
 * heading scale) and injects them as a <style id="ccuk-typography"> tag
 * that overrides the defaults in index.css.
 *
 * Same shape as themeColorsLoader: cache in localStorage for no-FOUC on
 * first paint, refresh from the network on mount. Used by both the App
 * bootstrap and the admin editor's live-preview hook.
 */
const API_URL = window.__BACKEND_URL__;
const CACHE_KEY = 'ccuk_typography';
const STYLE_TAG_ID = 'ccuk-typography';

import { safeSetItem } from './safeStorage';

function buildOverrideCss(typo) {
  if (!typo) return '';
  const lines = [];
  if (typo.font_size_base != null) lines.push(`  --theme-font-size-base: ${Number(typo.font_size_base)}px;`);
  if (typo.font_size_small != null) lines.push(`  --theme-font-size-small: ${Number(typo.font_size_small)}px;`);
  if (typo.font_size_heading_scale != null) lines.push(`  --theme-font-size-heading-scale: ${Number(typo.font_size_heading_scale)};`);
  if (lines.length === 0) return '';
  return `:root {\n${lines.join('\n')}\n}`;
}

function applyToDom(typo) {
  const css = buildOverrideCss(typo);
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(typo) {
  safeSetItem(CACHE_KEY, JSON.stringify(typo));
}

export function applyCachedTypography() {
  const cached = readCache();
  if (cached) applyToDom(cached);
}

export async function refreshTypography() {
  try {
    const res = await fetch(`${API_URL}/api/settings/typography`);
    if (!res.ok) return null;
    const data = await res.json();
    const typo = {
      font_size_base: data.font_size_base,
      font_size_small: data.font_size_small,
      font_size_heading_scale: data.font_size_heading_scale,
    };
    writeCache(typo);
    applyToDom(typo);
    window.dispatchEvent(new CustomEvent('ccuk:typography-updated', { detail: typo }));
    return typo;
  } catch {
    return null;
  }
}

export function setTypographyInMemory(typo) {
  writeCache(typo);
  applyToDom(typo);
  window.dispatchEvent(new CustomEvent('ccuk:typography-updated', { detail: typo }));
}
